import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import apiClient, { handleApiError } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  CorrectiveAction,
  CorrectiveActionFilters,
  CreateCorrectiveActionPayload,
  PaginatedResponse,
  UpdateCorrectiveActionPayload,
} from '@/lib/types';

const BASE = '/api/v1/compliance/actions';

function cleanFilters(filters: CorrectiveActionFilters): Record<string, string> {
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = String(value);
    }
  });
  return params;
}

// ─── Queries ────────────────────────────────────────────────────────────────

export function useCorrectiveActions(filters: CorrectiveActionFilters = {}) {
  const cleaned = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.correctiveActions.list(cleaned),
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<CorrectiveAction>>(
        `${BASE}/`,
        { params: cleaned },
      );
      return data;
    },
  });
}

export function useCorrectiveAction(id: number) {
  return useQuery({
    queryKey: queryKeys.correctiveActions.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<CorrectiveAction>(`${BASE}/${id}/`);
      return data;
    },
    enabled: !!id,
  });
}

export function useOverdueActions() {
  return useQuery({
    queryKey: queryKeys.correctiveActions.overdue(),
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<CorrectiveAction>>(
        `${BASE}/list-overdue/`,
      );
      return data;
    },
  });
}

export function useMyActions() {
  return useQuery({
    queryKey: queryKeys.correctiveActions.assignedToMe(),
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<CorrectiveAction>>(
        `${BASE}/assigned-to-me/`,
      );
      return data;
    },
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useCreateCorrectiveAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateCorrectiveActionPayload) => {
      const { data } = await apiClient.post<CorrectiveAction>(`${BASE}/`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.correctiveActions.all });
    },
    onError: handleApiError,
  });
}

export function useUpdateCorrectiveAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateCorrectiveActionPayload & { id: number }) => {
      const { data } = await apiClient.patch<CorrectiveAction>(`${BASE}/${id}/`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.correctiveActions.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.correctiveActions.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.correctiveActions.overdue() });
      queryClient.invalidateQueries({ queryKey: queryKeys.correctiveActions.assignedToMe() });
    },
    onError: handleApiError,
  });
}
